import React from 'react';

import FormField from '@adsk/alloy-react-form-field';
import AlloyInlineEditor from '@adsk/alloy-react-inline-edit';
import { useLocale } from '@adsk/alloy-react-locale';

import { Wrapper } from './styles';

type InlineEditorProps = {
  value?: string;
  placeholder?: string;
  error?: React.ReactNode;
  errorVariant?: string;
  required?: boolean;
  autoSelect?: boolean;
  initialEditMode?: boolean;
  onChange: (value: string) => void;
  onValidate?: (value: string) => React.ReactNode | null | undefined;
  onEditExit?: (value?: string, editProps?: { canceled?: boolean }) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  style?: React.CSSProperties;
  className?: string;
};

const InlineEditor = React.forwardRef<HTMLDivElement, InlineEditorProps>(
  (
    {
      errorVariant = FormField.ERROR_VARIANTS.TOOLTIP,
      required = false,
      autoSelect = false,
      initialEditMode = false,
      onValidate,
      inputProps,
      style,
      className,
      error: _error,
      ...rest
    },
    ref
  ) => {
    const { T } = useLocale(({ locale }) => require(`../../../locale/${locale}.json`));
    const [error, setError] = React.useState<React.ReactNode>(null);
    const props: { inputProps?: any } = {};
    // Setup input props
    props.inputProps = {
      ...inputProps,
      onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
        if (inputProps?.onFocus) inputProps.onFocus(e);
        if (autoSelect) e.target?.select?.();
      },
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (required && !e.target.value) {
          setError(T.translate('message.fieldRequired'));
        } else if (onValidate) {
          setError(onValidate(e.target.value));
        }

        if (inputProps?.onChange) inputProps.onChange(e);
      }
    };

    return (
      <Wrapper error={error} errorVariant={errorVariant} style={style as any} className={className}>
        <AlloyInlineEditor
          data-testid="folder-inline-edit"
          ref={ref}
          showPencilOnHoverOnly
          error={!!error}
          style={{ whiteSpace: 'nowrap' }}
          initialEditMode={initialEditMode}
          inputStyle={{ fontWeight: 'normal' }}
          {...props}
          {...rest}
        />
      </Wrapper>
    );
  }
);

InlineEditor.displayName = 'InlineEditor';

export default Object.assign(InlineEditor, { ERRORS: FormField.ERROR_VARIANTS });
